import { gql } from '@/__generated__/gql';

export const DUE_DILIGENCE_FUNNEL_LIST_QUERY = gql(`
  query DueDiligencePageListDueDiligenceFunnels($data: DueDiligenceFunnelListDto!) {
    listDueDiligenceFunnels(data: $data) {
      id
      funnelStaticId
      name
      createdAt
      status
      records {
        id
        investment {
          name
          securityStatus
        }
      }
    }
  }
`);

export const DUE_DILIGENCE_FUNNEL_QUERY = gql(`
  query DueDiligencePageGetDueDiligenceFunnel($id: String!, $sort: DueDiligenceRecordsSortBy!, $order: DueDiligenceRecordsSortDirection!) {
    getDueDiligenceFunnel(id: $id) {
      id
      funnelStaticId
      name
      externalId
      tiers {
        id
        name
        order
        tasksCount
        records(sortInput: { sortBy: $sort, sortDirection: $order }) {
          id
          status
          investment {
            id
            name
            assetClass
            managerId
            finalClose
            securityStatus
          }
          owner {
            id
            firstName
            lastName
            email
          }
          activeTierOpenTasksCount
          activeTier {
            id
            name
            order
            tasksCount
          }
        }
      }
    }
  }
`);

export const EXPORT_DUE_DILIGENCE_CSV = gql(`
  query DueDiligencePageExportDueDiligenceCSV($id: String!) {
    exportDueDiligenceCSV(id: $id)
  }
`);

export const DUE_DILIGENCE_RECORD_QUERY = gql(`
  query DueDiligencePageGetDueDiligenceRecord($id: String!) {
    getDueDiligenceRecord(id: $id) {
      id
      activeTier {
        id
        name
        order
      }
      activeTierOpenTasksCount
      status
      approver {
        id
        firstName
        lastName
      }
      statusUpdatedAt
      note
      createdAt
      updatedAt
      investment {
        id
        name
        securityStatus
        description
      }
      owner {
        id
        firstName
        lastName
      }
      funnel {
        id
        funnelStaticId
        externalId
        name
        docxTemplateStorageKey
        tiers {
          id
          name
          order
          tasksCount
          data {
            id
            status
            approver {
              id
              firstName
              lastName
            }
            statusUpdatedAt
            note
            createdAt
            updatedAt
          }
          sections {
            id
            name
            order
            pdfLayout
            userCanScore
            data {
              id
              updatedAt
              completedBy {
                id
                firstName
                lastName
              }
              completedAt
              assignedTo {
                id
                firstName
                lastName
              }
              assignedAt
              assignedBy {
                id
                firstName
                lastName
              }
              note
              score
              completed
            }
            fields {
              id
              name
              type
              order
              required
              docxKey
              promptGroup {
                id
                prompts
                summaryPrompt
                contextPrompt
                rubricTemplates
                outputStructure
                useCustomContextPrompt
                useCustomSummaryPrompt
              }
              data {
                id
                value
                commentary
                linked
                attachments {
                  id
                  asset {
                    assetKey
                    url
                  }
                  includeInAutomatedContent
                }
              }
              linkedField {
                id
                name
                funnelTierSection {
                  funnelTier {
                    name
                  }
                }
              }
              AI {
                answers
                answerJson
                citations {
                  answer
                  prompt
                  filename
                  embeddingId
                  pageNumbers
                  fileReferences {
                    filename
                    pageNumbers
                  }
                }
                summary
              }
              labelerData {
                filename
                value
              }
            }
          }
          checklistItems {
            id
            name
            required
            data {
              id
              completed
              completedAt
              completedBy {
                id
                firstName
                lastName
              }
              note
              createdAt
              updatedAt
              assignedTo {
                id
                firstName
                lastName
              }
              assignedBy {
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`);

export const GET_LATEST_DUE_DILIGENCE_FUNNEL_QUERY = gql(`
  query DueDiligencePageGetLatestDueDiligenceFunnel($id: String!) {
    getDueDiligenceFunnel(id: $id) {
      latestFunnel {
        tiers {
          id
          name
          order
        }
      }
    }
  }
`);

export const DUE_DILIGENCE_RECORD_QUERY_MIN = gql(`
  query DueDiligencePageGetDueDiligenceRecordMin($id: String!) {
    getDueDiligenceRecord(id: $id) {
      id
      status
      manager
      investment {
        id
        name
        subAssetClasses
        targetIRR
        targetYield
        vintageYear
        finalClose
        assetClass
        tags
        contact
        referral
        description
        securityStatus
      }
      owner {
        id
        email
        firstName
        lastName
      }
      funnel {
        id
        funnelStaticId
        externalId
        name
      }
    }
  }
`);

export const ASSIGN_CHECKLIST_ITEM_MUTATION = gql(`
  mutation DueDiligencePageAssignDueDiligenceRecordChecklistItem($data: DueDiligenceRecordAssignChecklistItemDto!) {
    assignDueDiligenceRecordChecklistItem(data: $data) {
      id
      note
      assignedTo {
        id
        firstName
        lastName
      }
      assignedBy {
        id
        firstName
        lastName
      }
      updatedAt
    }
  }
`);

export const ASSIGN_SECTION_MUTATION = gql(`
  mutation DueDiligencePageAssignDueDiligenceRecordSection($data: DueDiligenceRecordAssignSectionDto!) {
    assignDueDiligenceRecordSection(data: $data) {
      id
      note
      assignedTo {
        id
        firstName
        lastName
      }
      assignedBy {
        id
        firstName
        lastName
      }
      updatedAt
    }
  }
`);

export const UPDATE_DUE_DILIGENCE_RECORD_SECTION_SCORE_MUTATION = gql(`
  mutation DueDiligencePageUpdateDueDiligenceRecordSectionScore($data: DueDiligenceRecordUpdateSectionScoreDto!) {
    updateDueDiligenceRecordSectionScore(data: $data) {
      id
      score
    }
  }
`);

export const LIST_USERS_QUERY = gql(`
  query DueDiligencePageListUsers($filters: ListUsersFilterDto!) {
    listUsers(filters: $filters) {
      id
      firstName
      lastName
      email
      roles
    }
  }
`);

export const UPDATE_FIELD_ANSWER_MUTATION = gql(`
  mutation DueDiligencePageUpdateDueDiligenceRecordFieldAnswer($data: DueDiligenceRecordFieldAnswerUpdateDto!) {
    updateDueDiligenceRecordFieldAnswer(data: $data) {
      id
      value
      commentary
    }
  }
`);

export const RERUN_AI_SUGGESTION_MUTATION = gql(`
  mutation DueDiligencePageRerunAISuggestion($data: DealAssistantRerunAISuggestionDto!) {
    rerunAISuggestion(data: $data) {
      status
    }
  }
`);

export const TOGGLE_SECTION_COMPLETION_MUTATION = gql(`
  mutation DueDiligencePageToggleDueDiligenceRecordSectionCompletion($data: DueDiligenceRecordToggleSectionCompletionDto!) {
    toggleDueDiligenceRecordSectionCompletion(data: $data) {
      id
      completed
      note
      completedBy {
        id
        firstName
        lastName
      }
      completedAt
    }
  }
`);

export const UPDATE_TIER_STATUS_MUTATION = gql(`
  mutation DueDiligencePageUpdateDueDiligenceRecordTierStatus($data: DueDiligenceRecordUpdateTierStatusDto!) {
    updateDueDiligenceRecordTierStatus(data: $data) {
      id
      note
    }
  }
`);

export const UPDATE_CHECKLIST_ITEM_STATUS_MUTATION = gql(`
  mutation DueDiligencePageToggleDueDiligenceRecordChecklistItem($data: DueDiligenceRecordToggleChecklistItemDto!) {
    toggleDueDiligenceRecordChecklistItem(data: $data) {
      id
      completed
      note
      completedBy {
        id
        firstName
        lastName
      }
      completedAt
    }
  }
`);

export const UPDATE_RECORD_STATUS_MUTATION = gql(`
  mutation DueDiligencePageUpdateDueDiligenceRecordStatus($data: DueDiligenceRecordUpdateStatusDto!) {
    updateDueDiligenceRecordStatus(data: $data) {
      id
      note
    }
  }
`);

export const CREATE_DUE_DILIGENCE_RECORD_MUTATION = gql(`
  mutation DueDiligencePageCreateDueDiligenceRecord($data: DueDiligenceRecordCreateDto!) {
    createDueDiligenceRecord(data: $data) {
      id
    }
  }
`);

export const CREATE_INVESTMENT_MUTATION = gql(`
  mutation DueDiligencePageCreateInvestment($data: InvestmentEntityCreateDto!) {
    createInvestment(data: $data) {
      id
      name
    }
  }
`);

export const UPDATE_INVESTMENT_MUTATION = gql(`
  mutation DueDiligencePageUpdateInvestment($data: InvestmentEntityUpdateDto!) {
    updateInvestment(data: $data) {
      id
    }
  }
`);

export const UPDATE_DUE_DILIGENCE_RECORD_MUTATION = gql(`
  mutation DueDiligencePageUpdateDueDiligenceRecord($data: DueDiligenceRecordUpdateDto!) {
    updateDueDiligenceRecord(data: $data) {
      id
    }
  }
`);

export const GET_URL_FOR_UPLOAD = gql(`
  query DueDiligencePageGetDueDiligenceRecordFieldAnswerAttachmentUploadUrl($data: DueDiligenceRecordFieldAnswerAttachmentUploadUrlDto!) {
    getDueDiligenceRecordFieldAnswerAttachmentUploadUrl(data: $data) {
      assetKey
      url
    }
  }
`);

export const ADD_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION = gql(`
  mutation DueDiligencePageAddDueDiligenceRecordFieldAnswerAttachment($data: DueDiligenceRecordFieldAnswerAddAttachmentDto!) {
    addDueDiligenceRecordFieldAnswerAttachment(data: $data) {
      id
      asset {
        assetKey
        url
      }
      includeInAutomatedContent
    }
  }
`);

export const DELETE_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION = gql(`
  mutation DueDiligencePageDeleteDueDiligenceRecordFieldAnswerAttachment($id: String!) {
    deleteDueDiligenceRecordFieldAnswerAttachment(id: $id)
  }
`);

export const GET_CURRENT_MANAGERS_QUERY = gql(`
  query DueDiligencePageGetCurrentManagers($withCommitments: Boolean) {
    getCurrentManagers(withCommitments: $withCommitments) {
      values
    }
  }
`);

export const GET_CURRENT_SUB_ASSET_CLASSES_QUERY = gql(`
  query DueDiligencePageGetCurrentSubAssetClasses {
    getCurrentSubAssetClasses {
      values
    }
  }
`);

export const GET_CURRENT_TAGS_QUERY = gql(`
  query DueDiligencePageGetCurrentTags {
    getCurrentTags {
      values
    }
  }
`);

export const RELINK_DUE_DILIGENCE_RECORD_FIELD_ANSWER_MUTATION = gql(`
  mutation DueDiligencePageRelinkDueDiligenceRecordFieldAnswer($id: String!) {
    relinkDueDiligenceRecordFieldAnswer(id: $id) {
      id
    }
  }
`);
